/**
 * HangupConfirmDialog Component
 *
 * Confirmation shown when the user presses the red "end call" button during
 * an active session. Unlike ExitConfirmDialog (back button), this menu has a
 * clear visual hierarchy: ending and evaluating is the primary action — it
 * is the natural conclusion of the call — while save-and-exit and cancel
 * are deliberately smaller escapes for accidental presses.
 *
 * In game mode there is no "call" and no evaluation screen: this is the end of
 * an episode, so the copy talks about finishing the episode and moving on to
 * the next one instead.
 */

import { useTranslation } from 'react-i18next';

interface HangupConfirmDialogProps {
  onEndAndEvaluate: () => void;
  // Optional: if omitted, the "save and exit" option is hidden.
  onSaveAndExit?: () => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
  // In game mode, swap the call/evaluation copy for episode copy.
  isGameMode?: boolean;
}

export default function HangupConfirmDialog({
  onEndAndEvaluate,
  onSaveAndExit,
  onCancel,
  isSaving = false,
  isGameMode = false,
}: HangupConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-auto">
      <div className="text-center px-4 max-w-sm w-full">
        <div className="bg-gray-800/95 backdrop-blur-md rounded-2xl px-6 py-6 border border-white/10 shadow-2xl">
          {/* Icon */}
          <div className="text-4xl mb-3">📞</div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white mb-2">
            {isGameMode
              ? t('scenario.session.hangupConfirm.gameTitle', 'Terminar episódio?')
              : t('scenario.session.hangupConfirm.title', 'Terminar a chamada?')}
          </h2>

          {/* Description */}
          <p className="text-white/70 text-sm mb-6">
            {isGameMode
              ? t(
                  'scenario.session.hangupConfirm.gameDescription',
                  'Pronto para o próximo episódio?'
                )
              : t(
                  'scenario.session.hangupConfirm.description',
                  'Pronto para ver a tua avaliação?'
                )}
          </p>

          {/* Primary action — large, prominent */}
          <button
            onClick={onEndAndEvaluate}
            disabled={isSaving}
            className="w-full px-6 py-4 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold text-base rounded-xl transition-colors shadow-lg hover:shadow-xl mb-4"
          >
            {isGameMode
              ? t('scenario.session.hangupConfirm.gameEnd', 'Terminar')
              : t('scenario.session.hangupConfirm.endAndEvaluate', 'Terminar e Avaliar')}
          </button>

          {/* Secondary action — smaller, muted */}
          {onSaveAndExit && (
            <button
              onClick={onSaveAndExit}
              disabled={isSaving}
              className="w-full px-4 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-60 text-white/85 font-medium text-sm rounded-lg border border-white/10 transition-colors mb-2"
            >
              {isSaving
                ? t('scenario.session.saving', 'A guardar...')
                : t('scenario.session.hangupConfirm.saveAndExit', 'Guardar e sair')}
            </button>
          )}

          {/* Tertiary action — discreet text link */}
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="w-full px-4 py-1.5 text-white/50 hover:text-white/80 disabled:opacity-60 text-sm transition-colors"
          >
            {t('scenario.session.hangupConfirm.cancel', 'Voltar à conversa')}
          </button>
        </div>
      </div>
    </div>
  );
}
